import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogIn, LayoutDashboard } from "lucide-react";
import stGeorgeLogoLight from "@/assets/st-george-real-estate-logo-light.webp";
import { useAuthUser } from "@/hooks/useAuthUser";
import { trackCallClick } from "@/lib/analytics";

const Navigation = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(0);
  const [isScrolledPastHero, setIsScrolledPastHero] = useState(false);
  const { isAuthenticated, signOut } = useAuthUser();

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      const opacity = Math.min(scrollPosition / heroHeight, 0.95);
      setScrollOpacity(opacity);
      setIsScrolledPastHero(scrollPosition > heroHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: "SEARCH", href: "/search" },
    { label: "BUY", href: "/buy" },
    { label: "SELL", href: "/sell" },
    { label: "COMMUNITIES", href: "/communities" },
    { label: "ABOUT", href: "/about" },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-sm"
      style={{
        backgroundColor: `rgba(30, 20, 15, ${scrollOpacity * 0.95})`,
        borderBottom: scrollOpacity > 0.1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
        minHeight: '72px'
      }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between md:justify-center relative">
          {/* Desktop Navigation - Left */}
          <div className={`hidden md:flex items-center space-x-8 absolute left-0 transition-all duration-500 ${
            isScrolledPastHero ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform -translate-x-full'
          }`}>
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={item.label === "SEARCH"
                  ? "text-sm bg-primary text-white hover:bg-primary/90 transition-all duration-300 tracking-wider px-4 py-2 rounded-md"
                  : "text-sm text-white hover:text-primary transition-colors duration-300 tracking-wider"
                }
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Logo - Center */}
          <div className="flex flex-col items-center">
            <a href="/" className="flex flex-col items-center">
              <img
                src={stGeorgeLogoLight}
                alt="St. George Utah real estate homes for sale logo"
                width={128}
                height={128}
                className={`transition-all duration-500 ${
                  isScrolledPastHero ? 'h-10' : 'h-14 md:h-16'
                } w-auto`}
              />
            </a>
          </div>

          {/* Right side: Market reports, phone, LOGIN / DASHBOARD */}
          <div className={`hidden md:flex items-center gap-3 absolute right-0 transition-all duration-500 ${
            isScrolledPastHero ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform translate-x-full'
          }`}>
            <a href="/market-reports">
              <Button variant="outline" className="bg-transparent border-transparent text-white hover:text-primary hover:border-transparent transition-colors duration-300">
                MARKET REPORTS
              </Button>
            </a>
            <a href="tel:+14357034422" onClick={() => trackCallClick("nav-desktop")}>
              <Button variant="outline" className="bg-transparent border-transparent text-primary hover:border-primary hover:bg-transparent hover:text-primary">
                (435) 703-4422
              </Button>
            </a>
            {isAuthenticated ? (
              <>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-primary text-white hover:bg-primary/90 tracking-wider"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  DASHBOARD
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => { await signOut(); navigate("/"); }}
                  className="bg-transparent border-white/40 text-white hover:bg-white hover:text-foreground"
                >
                  SIGN OUT
                </Button>
              </>
            ) : (
              <Button
                onClick={() => navigate("/auth")}
                className="bg-primary text-white hover:bg-primary/90 tracking-wider"
              >
                <LogIn className="h-4 w-4 mr-2" />
                LOGIN
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-white/20">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-sm text-white hover:text-primary transition-colors duration-300 tracking-wider"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a href="/market-reports" className="text-sm text-white hover:text-primary tracking-wider" onClick={() => setIsMenuOpen(false)}>
                MARKET REPORTS
              </a>
              {isAuthenticated ? (
                <>
                  <Button
                    onClick={() => { navigate("/dashboard"); setIsMenuOpen(false); }}
                    className="w-fit bg-primary text-white hover:bg-primary/90"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    DASHBOARD
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => { await signOut(); setIsMenuOpen(false); navigate("/"); }}
                    className="w-fit border-white/40 text-white hover:bg-white hover:text-foreground"
                  >
                    SIGN OUT
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => { navigate("/auth"); setIsMenuOpen(false); }}
                  className="w-fit bg-primary text-white hover:bg-primary/90"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  LOGIN
                </Button>
              )}
              <a href="tel:+14357034422" onClick={() => trackCallClick("nav-mobile")}>
                <Button variant="ghost" className="text-primary w-fit">
                  (435) 703-4422
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
